/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { DeviceDetailsProps } from "../types";
import { Card, CardContent } from "../../../ui/card";


const E2DeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Parse alarm data from attributes
  let alarms: any[] = [];
  
  try {
    if (parsedAttributes.alarms) {
      const parsedAlarms = typeof parsedAttributes.alarms === 'string' 
        ? JSON.parse(parsedAttributes.alarms) 
        : parsedAttributes.alarms;
      
      // Convert alarms to array format
      if (Array.isArray(parsedAlarms)) {
        alarms = parsedAlarms;
      } else if (typeof parsedAlarms === 'object' && parsedAlarms !== null) {
        alarms = Object.entries(parsedAlarms).map(([name, alarmData]: [string, any]) => ({
          name: name,
          ...(typeof alarmData === 'object' ? alarmData : {})
        }));
      }
    }
  } catch (error) {
    console.error("E2 Device - Error parsing alarms:", error);
    alarms = [];
  }

  // Check if device is disabled
  const isDeviceDisabled = 
    statusLabel === "Disabled" ||
    device.status === "DISABLED" ||
    parsedAttributes.enabled === "false" ||
    parsedAttributes.enabled === false ||
    parsedAttributes.enabled === "0" ||
    parsedAttributes.enabled === 0;

  const devSerialNumber = parsedAttributes.devSerialNumber || parsedAttributes.elementSerialNumber || device.serialNumber;

  const addParamAliases = (source: Record<string, any> = {}) => {
    const normalized: Record<string, any> = { ...source };
    Object.entries(source).forEach(([key, value]) => {
      if (!key.endsWith("Param") && normalized[`${key}Param`] === undefined) {
        normalized[`${key}Param`] = value;
      }
    });
    return normalized;
  };

  const eu2CodeToAlarmName: Record<string, string> = {
    "1": "powerInterruptionAlarm",
    "4": "overtemperatureAlarm",
    "6": "opticalLinkAlarm",
    "20": "op1TransceiverFailureAlarm",
    "21": "op2TransceiverFailureAlarm",
    "22": "op3TransceiverFailureAlarm",
    "23": "op4TransceiverFailureAlarm",
    "24": "op5TransceiverFailureAlarm",
    "25": "op6TransceiverFailureAlarm",
    "26": "op7TransceiverFailureAlarm",
    "27": "op8TransceiverFailureAlarm",
    "28": "opSlaveTransceiverFailureAlarm",
    "29": "opMasterTransceiverFailureAlarm",
    "38": "external1Alarm",
    "39": "external2Alarm",
    "40": "external3Alarm",
    "41": "external4Alarm",
    "87": "opticalPortSwitchAlarm",
    "147": "op9TransceiverFailureAlarm",
    "148": "op10TransceiverFailureAlarm",
    "149": "op11TransceiverFailureAlarm",
    "150": "op12TransceiverFailureAlarm",
    "151": "op13TransceiverFailureAlarm",
    "152": "op14TransceiverFailureAlarm",
  };

  const normalizeEu2AlarmName = (name: string) => {
    const coded = String(name || '').match(/^MA6200-G1_EU2_(\d+)$/i);
    if (!coded) return name;
    return eu2CodeToAlarmName[coded[1]] || name;
  };

  const normalizedAlarms = useMemo(
    () => alarms.map((alarm: any) => ({
      ...alarm,
      rawName: String(alarm?.name || ''),
      name: normalizeEu2AlarmName(alarm?.name),
    })),
    [alarms]
  );

  // Extract sections from attributes
  const systemInfo = addParamAliases(parsedAttributes.systemInfo || {});
  const lanConnectivity = addParamAliases(parsedAttributes.lanConnectivity || {});

  // Helper function to get alarm status color
  const getAlarmStatusColor = (alarms: any[], isDisabled: boolean) => {
    if (isDisabled) {
      return '#9e9e9e'; // Gray
    }

    if (!alarms || alarms.length === 0) {
      return '#4caf50'; // Green - no active alarm
    }

    const allAlarmsDisabled = alarms.every(alarm => {
      const hasNoStat = alarm.alarmStat === undefined || alarm.alarmStat === null;
      const severity = parseInt(alarm.alarmSeverity || '0');
      const isZeroSeverity = severity === 0 || severity === 65535;
      return hasNoStat || (alarm.alarmStat !== undefined && isZeroSeverity);
    });
    
    if (allAlarmsDisabled) {
      return '#9e9e9e'; // Gray - all alarms disabled/not configured
    }

    let hasActiveAlarm = false;
    let mostCriticalSeverity = 0;

    for (const alarm of alarms) {
      const stat = alarm.alarmStat;
      const severity = parseInt(alarm.alarmSeverity || '0');
      
      // Skip if severity is 65535 (not available)
      if (severity === 65535) continue;
      
      // Check if alarm is active (stat = 1 means active)
      if (stat === '1' || stat === 1) {
        hasActiveAlarm = true;
        if (severity > mostCriticalSeverity) {
          mostCriticalSeverity = severity;
        }
      }
    }

    if (!hasActiveAlarm) {
      return '#4caf50'; // Green - clear/no active alarms
    }

    // Map severity to colors
    if (mostCriticalSeverity >= 4) {
      return '#f44336'; // Red - Major/Critical
    } else if (mostCriticalSeverity >= 2) {
      return '#ff9800'; // Orange - Warning/Minor
    } else {
      return '#4caf50'; // Green
    }
  };

  const normalizeName = (value: string) => String(value || '').trim().toLowerCase();

  const eu2CodeByAlarmName: Record<string, string> = {
    op1transceiverfailurealarm: '20',
    op2transceiverfailurealarm: '21',
    op3transceiverfailurealarm: '22',
    op4transceiverfailurealarm: '23',
    op5transceiverfailurealarm: '24',
    op6transceiverfailurealarm: '25',
    op7transceiverfailurealarm: '26',
    op8transceiverfailurealarm: '27',
    opslavetransceiverfailurealarm: '28',
    opmastertransceiverfailurealarm: '29',
    external1alarm: '38',
    external2alarm: '39',
    external3alarm: '40',
    external4alarm: '41',
    op9transceiverfailurealarm: '147',
    op10transceiverfailurealarm: '148',
    op11transceiverfailurealarm: '149',
    op12transceiverfailurealarm: '150',
    op13transceiverfailurealarm: '151',
    op14transceiverfailurealarm: '152',
  };

  // Calculate optical port status based on transceiver failure alarms
  const getOpticalPortStatus = (alarmName: string) => {
    const normalizedTarget = normalizeName(alarmName);
    const expectedCode = eu2CodeByAlarmName[normalizedTarget];

    const exactMatchAlarms = normalizedAlarms.filter((alarm: any) => {
      const normalizedName = normalizeName(alarm.name);
      const normalizedRawName = normalizeName(alarm.rawName);
      return normalizedName === normalizedTarget || normalizedRawName === normalizedTarget;
    });

    const codedMatchAlarms = expectedCode
      ? normalizedAlarms.filter((alarm: any) => {
          const normalizedRawName = normalizeName(alarm.rawName);
          const codedMatch = normalizedRawName.match(/^ma6200-g1_eu2_(\d+)$/i);
          return !!codedMatch && codedMatch[1] === expectedCode;
        })
      : [];

    const portAlarms = exactMatchAlarms.length > 0 ? exactMatchAlarms : codedMatchAlarms;

    if (portAlarms.length <= 1) {
      return getAlarmStatusColor(portAlarms, isDeviceDisabled);
    }

    const getAlarmTimestamp = (alarm: any): number => {
      const candidates = [
        alarm?.updatedAt,
        alarm?.lastChangeTime,
        alarm?.lastOccurTime,
        alarm?.timestamp,
        alarm?.time,
      ];

      for (const value of candidates) {
        if (value === undefined || value === null || value === "") continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
        const parsed = Date.parse(String(value));
        if (!Number.isNaN(parsed)) return parsed;
      }

      return Number.NEGATIVE_INFINITY;
    };

    const latestAlarm = portAlarms.reduce((latest, current) => {
      const latestTs = getAlarmTimestamp(latest);
      const currentTs = getAlarmTimestamp(current);
      return currentTs >= latestTs ? current : latest;
    });

    return getAlarmStatusColor([latestAlarm], isDeviceDisabled);
  };

  // Get optical port RX power (divided by 100 for dBm)
  const getOpticalPortRxPower = (portKey: string) => {
    const value = lanConnectivity[portKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical port TX power (divided by 100 for dBm)
  const getOpticalPortTxPower = (portKey: string) => {
    const value = lanConnectivity[portKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Filter out optical port alarms and external alarms from general alarms
  const generalAlarms = normalizedAlarms.filter((alarm: any) => {
    const name = alarm.name || '';
    // Exclude alarms with port numbers (op1, op2, etc.), master/slave, and external alarms
    return !name.match(/op\d+TransceiverFailureAlarm/) && 
           !name.includes('opMasterTransceiverFailureAlarm') &&
           !name.includes('opSlaveTransceiverFailureAlarm') &&
           !name.match(/external\d+Alarm/);
  });

  return (
    <Box sx={{ height: '100%' }}>
      {/* Main Content */}
      <Box sx={{ 
        width: '100%', 
        overflowY: 'auto',
        overflowX: 'auto',
        backgroundColor: 'var(--background)',
        height: '100%'
      }}>
        <Box sx={{
          minWidth: '1400px',
          pt: 3,
          px: 3,
          pb: 1
        }}>
          <Box>
            {/* General Information Card */}
            <Card className="mb-3">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Information</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Model Number</p>
                    <p className="text-sm text-foreground">{parsedAttributes.devModelNumber || device.model || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Serial Number</p>
                    <p className="text-sm text-foreground">{devSerialNumber || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Software Version</p>
                    <p className="text-sm text-foreground">{parsedAttributes.softwareVersion || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Location</p>
                    <p className="text-sm text-foreground">{device.location || parsedAttributes.location || parsedAttributes.devSiteName || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Last Update</p>
                    <p className="text-sm text-foreground">{device.lastSeen || device.updatedAt || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Optical Ports Section */}
            <Card className="mb-4">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Optical Ports</p>
                {/* All Optical Ports in One Row: Master, Slave, [space], OP1-8 */}
                <Box sx={{
                  display: 'flex', 
                  gap: 1.5,
                  justifyContent: 'center',
                  flexWrap: 'nowrap',
                  pb: 1,
                }}>
                  {/* Master Port */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    minWidth: 80
                  }}>
                    {/* Power readings above port box */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      width: '100%',
                      mb: 0.3,
                      px: 0.3,
                      gap: 1.5
                    }}>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortRxPower('opmasterTransceiverRXpower') !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.2
                        }}>
                          {getOpticalPortRxPower('opmasterTransceiverRXpower')}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.8rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↓
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortTxPower('opmasterTransceiverTXpower') !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.2
                        }}>
                          {getOpticalPortTxPower('opmasterTransceiverTXpower')}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.8rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↑
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Port box */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      width: '100%',
                      p: 1.5,
                      border: '2px solid var(--border)',
                      borderRadius: 2,
                      backgroundColor: 'var(--card)',
                      boxShadow: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: 'var(--primary)',
                        cursor: 'pointer'
                      }
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 700,
                        color: 'var(--foreground)',
                        fontSize: '0.75rem'
                      }}>
                        Master
                      </Typography>
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: getOpticalPortStatus('opMasterTransceiverFailureAlarm'),
                        border: '2px solid var(--border)',
                        transition: 'all 0.2s ease'
                      }} />
                    </Box>
                  </Box>

                  {/* Slave Port */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    minWidth: 80
                  }}>
                    {/* Power readings above port box */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      width: '100%',
                      mb: 0.3,
                      px: 0.3,
                      gap: 1.5
                    }}>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortRxPower('opslaveTransceiverRXpower') !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.2
                        }}>
                          {getOpticalPortRxPower('opslaveTransceiverRXpower')}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.8rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↓
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortTxPower('opslaveTransceiverTXpower') !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.2
                        }}>
                          {getOpticalPortTxPower('opslaveTransceiverTXpower')}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.8rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↑
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Port box */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      width: '100%',
                      p: 1.5,
                      border: '2px solid var(--border)',
                      borderRadius: 2,
                      backgroundColor: 'var(--card)',
                      boxShadow: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: 'var(--primary)',
                        cursor: 'pointer'
                      }
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 700,
                        color: 'var(--foreground)',
                        fontSize: '0.75rem'
                      }}>
                        Slave
                      </Typography>
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: getOpticalPortStatus('opSlaveTransceiverFailureAlarm'),
                        border: '2px solid var(--border)',
                        transition: 'all 0.2s ease'
                      }} />
                    </Box>
                  </Box>

                  {/* Spacer between Master/Slave and OP1-14 */}
                  <Box sx={{ width: '30px' }} />

                  {/* OP1-14 Optical Ports */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((portNum) => (
                    <Box
                      key={portNum}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.3,
                        minWidth: 80
                      }}
                    >
                      {/* Power readings above port box */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        width: '100%',
                        mb: 0.3,
                        px: 0.3,
                        gap: 1.5
                      }}>
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}>
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            fontSize: '0.55rem',
                            color: getOpticalPortRxPower(`op${portNum}TransceiverRXpower`) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                            fontWeight: 600,
                            mb: 0.2
                          }}>
                            {getOpticalPortRxPower(`op${portNum}TransceiverRXpower`)}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: '0.8rem',
                            color: 'var(--primary)',
                            fontWeight: 700,
                            lineHeight: 1
                          }}>
                            ↓
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}>
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            fontSize: '0.55rem',
                            color: getOpticalPortTxPower(`op${portNum}TransceiverTXpower`) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                            fontWeight: 600,
                            mb: 0.2
                          }}>
                            {getOpticalPortTxPower(`op${portNum}TransceiverTXpower`)}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: '0.8rem',
                            color: 'var(--primary)',
                            fontWeight: 700,
                            lineHeight: 1
                          }}>
                            ↑
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Port box */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '100%',
                        p: 1.5,
                        border: '2px solid var(--border)',
                        borderRadius: 2,
                        backgroundColor: 'var(--card)',
                        boxShadow: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                          borderColor: 'var(--primary)',
                          cursor: 'pointer'
                        }
                      }}>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 700,
                          color: 'var(--foreground)',
                          fontSize: '0.75rem'
                        }}>
                          OP {portNum}
                        </Typography>
                        <Box sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: getOpticalPortStatus(`op${portNum}TransceiverFailureAlarm`),
                          border: '2px solid var(--border)',
                          transition: 'all 0.2s ease'
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* External Alarms Row */}
                <Box sx={{
                  display: 'flex', 
                  gap: 1.5,
                  justifyContent: 'center',
                  flexWrap: 'nowrap',
                  mt: 3,
                  pb: 1,
                }}>
                  {[1, 2, 3, 4].map((externalNum) => (
                    <Box
                      key={externalNum}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 80
                      }}
                    >
                      {/* Port box */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '100%',
                        p: 1.5,
                        border: '2px solid var(--border)',
                        borderRadius: 2,
                        backgroundColor: 'var(--card)',
                        boxShadow: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                          borderColor: 'var(--primary)',
                          cursor: 'pointer'
                        }
                      }}>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 700,
                          color: 'var(--foreground)',
                          fontSize: '0.75rem'
                        }}>
                          External {externalNum}
                        </Typography>
                        <Box sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: getOpticalPortStatus(`external${externalNum}Alarm`),
                          border: '2px solid var(--border)',
                          transition: 'all 0.2s ease'
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Alarms Section */}
            <Card className="mt-2 mb-0">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Alarms</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {generalAlarms.map((alarm) => {
                    const statusColor = getAlarmStatusColor([alarm], isDeviceDisabled);
                    const displayName = alarm.name
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str: string) => str.toUpperCase())
                      .replace(/\d+/g, '')
                      .replace(/\s+/g, ' ')
                      .trim();
                    return (
                      <div key={alarm.name} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: statusColor, border: '2px solid var(--border)' }}
                        />
                        <span className="text-xs font-medium">{displayName}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default E2DeviceDetails;
